/**
 * A hook that returns an array of breadcrumbs using the `SearchResultContext`.
 *
 * - If no `searchResult` is found from the `SearchResultContext`, returns an empty array.
 * - If any `searchResult` is found, the `searchResult` article category will be used as the initial breadcrumb.
 * - If the `searchResult` has a parent category, the parent category will be prepended into the array of breadcrumbs.
 *
 * @module
 */
import { useContext } from 'react';
import { SearchResultContext } from '../contexts';
import { getArticlePath, getCategoryPath } from '../utils/routes';
import { useTranslation } from './useTranslation';
/**
 *
 * # useSearchBreadcrumbs
 *
 * Usage example:
 *
 * ```javascript
 * import { useSearchBreadcrumbs } from '@elevio/kb-kit/lib/hooks';
 *
 * const MyBreadCrumbs = () => {
 *   const crumbs = useSearchBreadcrumbs();
 *   console.log(crumbs); // IBreadCrumbs
 *
 *   if (!crumbs || !crumbs.length) return null;
 *
 *   return (
 *     <div>
 *       {crumbs.map((crumb, index) => {
 *         const conditionalChevron = index < crumbs.length - 1 ? ` > ` : '';
 *         return (
 *           <span>
 *             `${crumb.title}${conditionalChevron}`
 *           </span>
 *         );
 *       })}
 *     </div>
 *   );
 * };
 * ```
 */
export function useSearchBreadcrumbs() {
    const searchResult = useContext(SearchResultContext);
    const { t } = useTranslation();
    if (!searchResult)
        return [];
    const results = [
        {
            linkType: 'article',
            linkId: searchResult.id,
            title: t('searchBreadCrumb.article', '{{article.title}}', {
                article: {
                    id: searchResult.id,
                    title: searchResult.title,
                    slug: searchResult.slug,
                },
            }),
            slug: searchResult.slug,
            path: getArticlePath({
                slug: searchResult.slug,
            }),
            isCurrent: false,
        },
        {
            linkType: 'category',
            linkId: searchResult.category.id,
            title: t('searchBreadCrumb.category', '{{category.title}}', {
                category: {
                    id: searchResult.category.id,
                    title: searchResult.category.title,
                    slug: searchResult.category.slug,
                },
            }),
            slug: searchResult.category.slug,
            path: getCategoryPath({
                slug: searchResult.category.slug,
            }),
            isCurrent: false,
        },
    ];
    if (searchResult.category.parentCategory) {
        results.unshift({
            linkType: 'category',
            linkId: searchResult.category.parentCategory.id,
            title: t('searchBreadCrumb.category', '{{category.title}}', {
                category: {
                    id: searchResult.category.parentCategory.id,
                    title: searchResult.category.parentCategory.title,
                    slug: searchResult.category.parentCategory.slug,
                },
            }),
            slug: searchResult.category.parentCategory.slug,
            path: getCategoryPath({
                slug: searchResult.category.parentCategory.slug,
            }),
            isCurrent: false,
        });
    }
    return results;
}
//# sourceMappingURL=useSearchBreadcrumbs.js.map